import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../context/AppContext';
import {
  GlobeAltIcon,
  EyeIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  PhoneIcon,
  ShieldExclamationIcon,
  ClipboardDocumentListIcon,
  TrashIcon,
  PencilIcon,
  ArrowUpTrayIcon,
  XMarkIcon,
  HeartIcon
} from '@heroicons/react/24/outline';

const DEFAULT_VISIBILITY = {
  plotAlias: true,
  plotSection: true,
  plotStatus: true,
  plotSize: false,  // Show calculated plot size (estimate)
  plotAvailability: true,  // Show available/reserved/sold plots
  interredName: true,
  interredDates: true,
  interredVeteranStatus: false,
  ownerName: false,
  ownerContact: false,
  plotAttachments: true,
  plotFlexFields: true
};

const DOCUMENT_CATEGORIES = [
  { value: 'rules', label: 'Rules & Regulations' },
  { value: 'pricing', label: 'Pricing Information' },
  { value: 'forms', label: 'Forms & Applications' },
  { value: 'photos', label: 'Photos' },
  { value: 'other', label: 'Other' }
];

const PublicPortalSettings = () => {
  const { token, user } = useContext(AppContext);
  const navigate = useNavigate();

  const [activeSection, setActiveSection] = useState('portal');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Settings state
  const [settings, setSettings] = useState({
    portalEnabled: false,
    visibility: DEFAULT_VISIBILITY,
    showPricing: false,
    showDocuments: true,
    showContactInfo: true,
    contactInfo: { address: '', phone: '', email: '', hours: '' },
    welcomeMessage: '',
    privacyPolicy: '',
    externalLinks: []
  });

  // Documents state
  const [documents, setDocuments] = useState([]);
  const [editingDocument, setEditingDocument] = useState(null);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const fileInputRef = useRef(null);

  // New link state
  const [newLink, setNewLink] = useState({ title: '', url: '' });

  // Audit logs state
  const [auditLogs, setAuditLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotalCount, setLogsTotalCount] = useState(0);
  const LOGS_PER_PAGE = 10;

  // Service tags for pricing exclusion
  const [serviceTags, setServiceTags] = useState([]);

  // Security check
  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  // Load settings
  useEffect(() => {
    if (token) {
      loadSettings();
      loadDocuments();
      loadServiceTags();
    }
  }, [token]);

  const loadServiceTags = async () => {
    try {
      // categoryId=2 is the Service category for product/service tags
      const response = await fetch('/api/statuses?categoryId=2', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setServiceTags(data);
      }
    } catch (err) {
      console.error('Failed to load service tags:', err);
    }
  };

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/public-settings', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({ ...prev, ...data }));
      }
    } catch (err) {
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const loadDocuments = async () => {
    try {
      const response = await fetch('/api/public-settings/documents', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDocuments(data);
      }
    } catch (err) {
      console.error('Failed to load documents:', err);
    }
  };

  const loadAuditLogs = async (page = 1) => {
    try {
      setLogsLoading(true);
      const offset = (page - 1) * LOGS_PER_PAGE;
      const response = await fetch(`/api/public-settings/audit-logs?category=public_portal&limit=${LOGS_PER_PAGE}&offset=${offset}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAuditLogs(data.rows || []);
        setLogsTotalCount(data.count || 0);
        setLogsPage(page);
      }
    } catch (err) {
      console.error('Failed to load audit logs:', err);
    } finally {
      setLogsLoading(false);
    }
  };

  const saveSettings = async (updates = null) => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const dataToSave = updates || settings;

      const response = await fetch('/api/public-settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(dataToSave)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save settings');
      }

      const updatedSettings = await response.json();
      setSettings(prev => ({ ...prev, ...updatedSettings }));
      setSuccess('Settings saved successfully');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (key) => {
    const newValue = !settings[key];
    setSettings(prev => ({ ...prev, [key]: newValue }));
    await saveSettings({ [key]: newValue });
  };

  const handleVisibilityChange = (key) => {
    const newVisibility = { ...settings.visibility, [key]: !settings.visibility[key] };
    setSettings(prev => ({ ...prev, visibility: newVisibility }));
  };

  const handleContactInfoChange = (key, value) => {
    const newContactInfo = { ...settings.contactInfo, [key]: value };
    setSettings(prev => ({ ...prev, contactInfo: newContactInfo }));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploadingDocument(true);
      setError('');

      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', file.name.replace(/\.[^/.]+$/, ''));
      formData.append('category', 'other');
      formData.append('isPublic', 'true');

      const response = await fetch('/api/public-settings/documents', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to upload document');
      }

      const newDoc = await response.json();
      setDocuments(prev => [...prev, newDoc]);
      setSuccess('Document uploaded successfully');

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleUpdateDocument = async (doc) => {
    try {
      const response = await fetch(`/api/public-settings/documents/${doc.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(doc)
      });

      if (!response.ok) {
        throw new Error('Failed to update document');
      }

      const updated = await response.json();
      setDocuments(prev => prev.map(d => d.id === updated.id ? updated : d));
      setEditingDocument(null);
      setSuccess('Document updated successfully');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteDocument = async (id) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      const response = await fetch(`/api/public-settings/documents/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to delete document');
      }

      setDocuments(prev => prev.filter(d => d.id !== id));
      setSuccess('Document deleted successfully');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddLink = () => {
    if (!newLink.title || !newLink.url) return;

    const links = [...(settings.externalLinks || []), newLink];
    setSettings(prev => ({ ...prev, externalLinks: links }));
    setNewLink({ title: '', url: '' });
  };

  const handleRemoveLink = (index) => {
    const links = settings.externalLinks.filter((_, i) => i !== index);
    setSettings(prev => ({ ...prev, externalLinks: links }));
  };

  const handleToggleExcludedTag = (tagId) => {
    const currentExcluded = settings.pricingExcludedTags || [];
    let newExcluded;
    if (currentExcluded.includes(tagId)) {
      newExcluded = currentExcluded.filter(id => id !== tagId);
    } else {
      newExcluded = [...currentExcluded, tagId];
    }
    setSettings(prev => ({ ...prev, pricingExcludedTags: newExcluded }));
  };

  if (!user || user.role !== 'admin') {
    return (
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <ShieldExclamationIcon className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-2 text-lg font-semibold text-gray-900">Access Denied</h3>
          <p className="mt-1 text-sm text-gray-500">Admin access required.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-500">Loading settings...</p>
        </div>
      </div>
    );
  }

  const navItems = [
    { id: 'portal', label: 'Portal Settings', icon: GlobeAltIcon },
    { id: 'visibility', label: 'Data Visibility', icon: EyeIcon },
    { id: 'obituaries', label: 'Obituaries', icon: HeartIcon },
    { id: 'documents', label: 'Documents', icon: DocumentTextIcon },
    { id: 'pricing', label: 'Pricing', icon: CurrencyDollarIcon },
    { id: 'contact', label: 'Contact Info', icon: PhoneIcon },
    { id: 'logs', label: 'Audit Logs', icon: ClipboardDocumentListIcon }
  ];

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Public Portal Settings</h1>
        <p className="mt-2 text-sm text-gray-700">
          Configure the public-facing cemetery portal for visitors and family members.
        </p>
        {settings.portalEnabled && (
          <div className="mt-3">
            <a
              href="/public"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-500"
            >
              <GlobeAltIcon className="h-4 w-4 mr-1" />
              View Public Portal
            </a>
          </div>
        )}
      </div>

      <div className="lg:grid lg:grid-cols-12 lg:gap-x-8">
        <aside className="lg:col-span-3">
          <nav className="space-y-1">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  setActiveSection(item.id);
                  if (item.id === 'logs') loadAuditLogs();
                }}
                className={`group flex w-full items-center rounded-md px-3 py-2 text-sm font-medium ${
                  activeSection === item.id
                    ? 'bg-indigo-50 text-indigo-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <item.icon
                  className={`-ml-1 mr-3 h-6 w-6 flex-shrink-0 ${
                    activeSection === item.id ? 'text-indigo-600' : 'text-gray-400'
                  }`}
                />
                <span className="truncate">{item.label}</span>
              </button>
            ))}
          </nav>
        </aside>

        <main className="mt-8 lg:col-span-9 lg:mt-0">
          {error && (
            <div className="mb-4 rounded-md bg-red-50 p-4">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-4 rounded-md bg-green-50 p-4">
              <p className="text-sm text-green-700">{success}</p>
            </div>
          )}

          {/* Portal Settings Section */}
          {activeSection === 'portal' && (
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium leading-6 text-gray-900">Portal Configuration</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Enable or disable the public portal and configure its features.
                </p>

                <div className="mt-6 space-y-6">
                  {/* Enable Portal Toggle */}
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">Enable Public Portal</h4>
                      <p className="text-xs text-gray-500">Allow public access to cemetery information</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleToggle('portalEnabled')}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2 ${
                        settings.portalEnabled ? 'bg-indigo-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          settings.portalEnabled ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Show Documents Toggle */}
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">Show Documents</h4>
                      <p className="text-xs text-gray-500">Make uploaded documents available to visitors</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleToggle('showDocuments')}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2 ${
                        settings.showDocuments ? 'bg-indigo-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          settings.showDocuments ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Show Contact Info Toggle */}
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">Show Contact Information</h4>
                      <p className="text-xs text-gray-500">Display cemetery contact details</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleToggle('showContactInfo')}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2 ${
                        settings.showContactInfo ? 'bg-indigo-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          settings.showContactInfo ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Welcome Message */}
                  <div className="border-t border-gray-200 pt-6">
                    <label htmlFor="welcomeMessage" className="block text-sm font-medium text-gray-900">
                      Welcome Message
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      Displayed at the top of the public portal
                    </p>
                    <textarea
                      id="welcomeMessage"
                      rows={3}
                      value={settings.welcomeMessage}
                      onChange={(e) => setSettings(prev => ({ ...prev, welcomeMessage: e.target.value }))}
                      className="mt-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      placeholder="Welcome to our cemetery..."
                    />
                  </div>

                  {/* Privacy Policy */}
                  <div>
                    <label htmlFor="privacyPolicy" className="block text-sm font-medium text-gray-900">
                      Privacy Policy
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      Privacy notice displayed in the portal footer
                    </p>
                    <textarea
                      id="privacyPolicy"
                      rows={4}
                      value={settings.privacyPolicy}
                      onChange={(e) => setSettings(prev => ({ ...prev, privacyPolicy: e.target.value }))}
                      className="mt-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      placeholder="Information about how visitor data is handled..."
                    />
                  </div>

                  {/* External Links */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">External Links</h4>
                    <p className="text-xs text-gray-500 mt-1">Links displayed in the portal navigation</p>

                    <div className="mt-3 space-y-2">
                      {(settings.externalLinks || []).map((link, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <input
                            type="text"
                            value={link.title}
                            readOnly
                            className="flex-1 rounded-md border-gray-300 bg-gray-50 text-sm"
                          />
                          <input
                            type="text"
                            value={link.url}
                            readOnly
                            className="flex-1 rounded-md border-gray-300 bg-gray-50 text-sm"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveLink(index)}
                            className="p-1 text-red-600 hover:text-red-500"
                          >
                            <XMarkIcon className="h-5 w-5" />
                          </button>
                        </div>
                      ))}

                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={newLink.title}
                          onChange={(e) => setNewLink(prev => ({ ...prev, title: e.target.value }))}
                          placeholder="Link title"
                          className="flex-1 rounded-md border-gray-300 text-sm"
                        />
                        <input
                          type="url"
                          value={newLink.url}
                          onChange={(e) => setNewLink(prev => ({ ...prev, url: e.target.value }))}
                          placeholder="https://..."
                          className="flex-1 rounded-md border-gray-300 text-sm"
                        />
                        <button
                          type="button"
                          onClick={handleAddLink}
                          disabled={!newLink.title || !newLink.url}
                          className="px-3 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-500 disabled:opacity-50"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end pt-4">
                    <button
                      type="button"
                      onClick={() => saveSettings()}
                      disabled={saving}
                      className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50"
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Data Visibility Section */}
          {activeSection === 'visibility' && (
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium leading-6 text-gray-900">Data Visibility</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Control which fields are visible to public visitors.
                </p>

                <div className="mt-6 space-y-4">
                  <h4 className="text-sm font-medium text-gray-900 border-b pb-2">Plot Information</h4>

                  {[
                    { key: 'plotAlias', label: 'Plot Alias/ID', desc: 'Show plot identifier' },
                    { key: 'plotSection', label: 'Section Name', desc: 'Show cemetery section' },
                    { key: 'plotStatus', label: 'Plot Status', desc: 'Show availability status' },
                    { key: 'plotSize', label: 'Calculated Plot Size', desc: 'Show estimated plot size (calculated from GIS dimensions, may not reflect actual surveyed size)' },
                    { key: 'plotAvailability', label: 'Show Available Plots', desc: 'Display available, reserved, and sold plots on map' },
                    { key: 'plotAttachments', label: 'Plot Attachments', desc: 'Show public images, documents, and links on plot details' },
                    { key: 'plotFlexFields', label: 'Custom Fields', desc: 'Show public custom field values on plot details' }
                  ].map(item => (
                    <div key={item.key} className="flex items-center justify-between py-2">
                      <div>
                        <span className="text-sm text-gray-900">{item.label}</span>
                        <p className="text-xs text-gray-500">{item.desc}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleVisibilityChange(item.key)}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                          settings.visibility[item.key] ? 'bg-indigo-600' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                            settings.visibility[item.key] ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>
                  ))}

                  <h4 className="text-sm font-medium text-gray-900 border-b pb-2 pt-4">Interment Information</h4>

                  {[
                    { key: 'interredName', label: 'Interred Name', desc: 'Show names of those interred' },
                    { key: 'interredDates', label: 'Interment Dates', desc: 'Show burial dates' },
                    { key: 'interredVeteranStatus', label: 'Veteran Status', desc: 'Show veteran designation' }
                  ].map(item => (
                    <div key={item.key} className="flex items-center justify-between py-2">
                      <div>
                        <span className="text-sm text-gray-900">{item.label}</span>
                        <p className="text-xs text-gray-500">{item.desc}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleVisibilityChange(item.key)}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                          settings.visibility[item.key] ? 'bg-indigo-600' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                            settings.visibility[item.key] ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>
                  ))}

                  <h4 className="text-sm font-medium text-gray-900 border-b pb-2 pt-4">Owner Information</h4>

                  {[
                    { key: 'ownerName', label: 'Owner Name', desc: 'Show plot owner names' },
                    { key: 'ownerContact', label: 'Owner Contact', desc: 'Show owner contact information' }
                  ].map(item => (
                    <div key={item.key} className="flex items-center justify-between py-2">
                      <div>
                        <span className="text-sm text-gray-900">{item.label}</span>
                        <p className="text-xs text-gray-500">{item.desc}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleVisibilityChange(item.key)}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                          settings.visibility[item.key] ? 'bg-indigo-600' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                            settings.visibility[item.key] ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>
                  ))}

                  <div className="flex justify-end pt-4">
                    <button
                      type="button"
                      onClick={() => saveSettings()}
                      disabled={saving}
                      className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50"
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Obituaries Section */}
          {activeSection === 'obituaries' && (
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium leading-6 text-gray-900">Obituary Settings</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Configure how obituaries are displayed on the public portal.
                </p>

                <div className="mt-6 space-y-6">
                  {/* Enable Obituaries Toggle */}
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">Enable Obituaries Page</h4>
                      <p className="text-xs text-gray-500">Show the obituaries tab on the public portal</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleToggle('showObituaries')}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2 ${
                        settings.showObituaries ? 'bg-indigo-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          settings.showObituaries ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Show Obituary on Plot Details Toggle */}
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">Show Obituary Link on Plot Details</h4>
                      <p className="text-xs text-gray-500">Display a link to the obituary when viewing plot details for interred individuals</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleToggle('showObituaryOnPlot')}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2 ${
                        settings.showObituaryOnPlot ? 'bg-indigo-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          settings.showObituaryOnPlot ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>

                  {settings.showObituaries && (
                    <>
                      {/* Page Title */}
                      <div className="border-t border-gray-200 pt-6">
                        <label htmlFor="obituaryPageTitle" className="block text-sm font-medium text-gray-900">
                          Page Title
                        </label>
                        <p className="text-xs text-gray-500 mt-1">
                          Title displayed at the top of the obituaries page
                        </p>
                        <input
                          type="text"
                          id="obituaryPageTitle"
                          value={settings.obituaryPageTitle || ''}
                          onChange={(e) => setSettings(prev => ({ ...prev, obituaryPageTitle: e.target.value }))}
                          className="mt-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          placeholder="In Memoriam"
                        />
                      </div>

                      {/* Page Description */}
                      <div>
                        <label htmlFor="obituaryPageDescription" className="block text-sm font-medium text-gray-900">
                          Page Description
                        </label>
                        <p className="text-xs text-gray-500 mt-1">
                          Introductory text shown on the obituaries page
                        </p>
                        <textarea
                          id="obituaryPageDescription"
                          rows={3}
                          value={settings.obituaryPageDescription || ''}
                          onChange={(e) => setSettings(prev => ({ ...prev, obituaryPageDescription: e.target.value }))}
                          className="mt-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          placeholder="Honoring the memory of those laid to rest here..."
                        />
                      </div>

                      {/* Obituary Detail Visibility */}
                      <div className="border-t border-gray-200 pt-6">
                        <h4 className="text-sm font-medium text-gray-900 mb-2">Obituary Detail Visibility</h4>
                        <p className="text-xs text-gray-500 mb-4">
                          Control which sections are visible on public obituary pages (data must also be provided in the obituary)
                        </p>

                        <div className="space-y-4">
                          {[
                            { key: 'biography', label: 'Biography', desc: 'Show the full obituary text' },
                            { key: 'birthplace', label: 'Birthplace', desc: 'Show place of birth' },
                            { key: 'residence', label: 'Residence', desc: 'Show place of residence' },
                            { key: 'serviceInfo', label: 'Service Information', desc: 'Show service date, location, and notes' },
                            { key: 'condolences', label: 'Condolences Address', desc: 'Show where to send condolences' },
                            { key: 'memorialDonations', label: 'Memorial Donations', desc: 'Show memorial donation information' },
                            { key: 'lifePhotos', label: 'Life Photos', desc: 'Show uploaded photos in a gallery' },
                            { key: 'documents', label: 'Documents', desc: 'Allow downloading attached documents' }
                          ].map(item => {
                            const visibility = settings.obituaryVisibility || {};
                            const isEnabled = visibility[item.key] !== false; // default to true
                            return (
                              <div key={item.key} className="flex items-center justify-between py-2">
                                <div>
                                  <span className="text-sm text-gray-900">{item.label}</span>
                                  <p className="text-xs text-gray-500">{item.desc}</p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newVisibility = {
                                      ...(settings.obituaryVisibility || {}),
                                      [item.key]: !isEnabled
                                    };
                                    setSettings(prev => ({ ...prev, obituaryVisibility: newVisibility }));
                                  }}
                                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                                    isEnabled ? 'bg-indigo-600' : 'bg-gray-200'
                                  }`}
                                >
                                  <span
                                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                                      isEnabled ? 'translate-x-5' : 'translate-x-0'
                                    }`}
                                  />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  )}

                  <div className="flex justify-end pt-4">
                    <button
                      type="button"
                      onClick={() => saveSettings()}
                      disabled={saving}
                      className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50"
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Documents Section */}
          {activeSection === 'documents' && (
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium leading-6 text-gray-900">Public Documents</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Upload documents for public access (rules, forms, pricing, etc.)
                    </p>
                  </div>
                  <div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      className="hidden"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingDocument}
                      className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50"
                    >
                      <ArrowUpTrayIcon className="h-4 w-4 mr-1" />
                      {uploadingDocument ? 'Uploading...' : 'Upload Document'}
                    </button>
                  </div>
                </div>

                <div className="mt-6">
                  {documents.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                      <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-semibold text-gray-900">No documents</h3>
                      <p className="mt-1 text-sm text-gray-500">Upload documents to share with visitors.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {documents.map(doc => (
                        <div key={doc.id} className="border rounded-lg p-4">
                          {editingDocument?.id === doc.id ? (
                            <div className="space-y-3">
                              <input
                                type="text"
                                value={editingDocument.title}
                                onChange={(e) => setEditingDocument(prev => ({ ...prev, title: e.target.value }))}
                                className="block w-full rounded-md border-gray-300 text-sm"
                                placeholder="Document title"
                              />
                              <textarea
                                value={editingDocument.description || ''}
                                onChange={(e) => setEditingDocument(prev => ({ ...prev, description: e.target.value }))}
                                rows={2}
                                className="block w-full rounded-md border-gray-300 text-sm"
                                placeholder="Description (optional)"
                              />
                              <select
                                value={editingDocument.category}
                                onChange={(e) => setEditingDocument(prev => ({ ...prev, category: e.target.value }))}
                                className="block w-full rounded-md border-gray-300 text-sm"
                              >
                                {DOCUMENT_CATEGORIES.map(cat => (
                                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                                ))}
                              </select>
                              <div className="flex items-center gap-2">
                                <label className="flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={editingDocument.isPublic}
                                    onChange={(e) => setEditingDocument(prev => ({ ...prev, isPublic: e.target.checked }))}
                                    className="rounded border-gray-300 text-indigo-600"
                                  />
                                  <span className="ml-2 text-sm text-gray-700">Public</span>
                                </label>
                              </div>
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleUpdateDocument(editingDocument)}
                                  className="px-3 py-1 text-sm font-medium text-white bg-indigo-600 rounded hover:bg-indigo-500"
                                >
                                  Save
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setEditingDocument(null)}
                                  className="px-3 py-1 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-start justify-between">
                              <div>
                                <h4 className="text-sm font-medium text-gray-900">{doc.title}</h4>
                                {doc.description && (
                                  <p className="mt-1 text-xs text-gray-500">{doc.description}</p>
                                )}
                                <div className="mt-2 flex items-center gap-3 text-xs text-gray-500">
                                  <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5">
                                    {DOCUMENT_CATEGORIES.find(c => c.value === doc.category)?.label || doc.category}
                                  </span>
                                  <span>{doc.fileName}</span>
                                  <span>{(doc.fileSize / 1024).toFixed(1)} KB</span>
                                  <span className={doc.isPublic ? 'text-green-600' : 'text-gray-400'}>
                                    {doc.isPublic ? 'Public' : 'Private'}
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => setEditingDocument({ ...doc })}
                                  className="p-1 text-gray-400 hover:text-gray-600"
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteDocument(doc.id)}
                                  className="p-1 text-red-400 hover:text-red-600"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Pricing Section */}
          {activeSection === 'pricing' && (
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium leading-6 text-gray-900">Pricing Display</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Configure whether pricing information is visible to the public.
                </p>

                <div className="mt-6 space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">Show Pricing</h4>
                      <p className="text-xs text-gray-500">Display product prices on the public portal</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleToggle('showPricing')}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                        settings.showPricing ? 'bg-indigo-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                          settings.showPricing ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>

                  {settings.showPricing && (
                    <>
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-600">
                          Products configured in the admin panel will be displayed with their prices.
                          <a href="/products" className="ml-1 text-indigo-600 hover:text-indigo-500">
                            Manage Products
                          </a>
                        </p>
                      </div>

                      {/* Show Tags Toggle */}
                      <div className="flex items-center justify-between border-t pt-4">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900">Show Service Tags</h4>
                          <p className="text-xs text-gray-500">Display associated tags with each product/service</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleToggle('pricingShowTags')}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                            settings.pricingShowTags ? 'bg-indigo-600' : 'bg-gray-200'
                          }`}
                        >
                          <span
                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                              settings.pricingShowTags ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>

                      {/* Show Filters Toggle */}
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900">Show Tag Filters</h4>
                          <p className="text-xs text-gray-500">Allow visitors to filter products by service tags</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleToggle('pricingShowFilters')}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                            settings.pricingShowFilters ? 'bg-indigo-600' : 'bg-gray-200'
                          }`}
                        >
                          <span
                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                              settings.pricingShowFilters ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>

                      {/* Excluded Tags */}
                      <div className="border-t pt-4">
                        <h4 className="text-sm font-medium text-gray-900">Excluded Service Tags</h4>
                        <p className="text-xs text-gray-500 mt-1">
                          Select any of the service tags below to hide matching services from the public pricing page
                        </p>
                        {serviceTags.length > 0 ? (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {serviceTags.map(tag => {
                              const isExcluded = (settings.pricingExcludedTags || []).includes(tag.id);
                              return (
                                <button
                                  key={tag.id}
                                  type="button"
                                  onClick={() => handleToggleExcludedTag(tag.id)}
                                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                                    isExcluded
                                      ? 'bg-red-100 text-red-800 hover:bg-red-200'
                                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                  }`}
                                >
                                  {tag.name}
                                  {isExcluded && (
                                    <span className="ml-1 text-red-600">×</span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="mt-3 text-sm text-gray-500 italic">
                            No service tags configured. <a href="/statuses" className="text-indigo-600 hover:text-indigo-500">Manage Service Tags</a>
                          </p>
                        )}
                        {(settings.pricingExcludedTags || []).length > 0 && (
                          <p className="mt-2 text-xs text-amber-600">
                            {(settings.pricingExcludedTags || []).length} tag(s) excluded - products with these tags won't appear on the public portal
                          </p>
                        )}
                      </div>

                      <div className="flex justify-end pt-4">
                        <button
                          type="button"
                          onClick={() => saveSettings()}
                          disabled={saving}
                          className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50"
                        >
                          {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Contact Info Section */}
          {activeSection === 'contact' && (
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium leading-6 text-gray-900">Contact Information</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Cemetery contact details displayed on the public portal. The cemetery name uses the Site Name from Site Settings.
                </p>

                <div className="mt-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Address</label>
                    <textarea
                      value={settings.contactInfo.address}
                      onChange={(e) => handleContactInfoChange('address', e.target.value)}
                      rows={2}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Phone</label>
                      <input
                        type="tel"
                        value={settings.contactInfo.phone}
                        onChange={(e) => handleContactInfoChange('phone', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Email</label>
                      <input
                        type="email"
                        value={settings.contactInfo.email}
                        onChange={(e) => handleContactInfoChange('email', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Office Hours</label>
                    <input
                      type="text"
                      value={settings.contactInfo.hours}
                      onChange={(e) => handleContactInfoChange('hours', e.target.value)}
                      placeholder="Mon-Fri 9am-5pm"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>

                  <div className="flex justify-end pt-4">
                    <button
                      type="button"
                      onClick={() => saveSettings()}
                      disabled={saving}
                      className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50"
                    >
                      {saving ? 'Saving...' : 'Save Contact Info'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Audit Logs Section */}
          {activeSection === 'logs' && (
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium leading-6 text-gray-900">Public Portal Activity</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Recent visitor activity on the public portal.
                </p>

                <div className="mt-6">
                  {logsLoading ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mx-auto"></div>
                    </div>
                  ) : auditLogs.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                      <ClipboardDocumentListIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-semibold text-gray-900">No activity yet</h3>
                      <p className="mt-1 text-sm text-gray-500">Public portal activity will appear here.</p>
                    </div>
                  ) : (
                    <div className="overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {auditLogs.map(log => (
                            <tr key={log.id}>
                              <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                                {new Date(log.createdAt).toLocaleString()}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                {log.action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                              </td>
                              <td className="px-4 py-3 text-xs text-gray-500">
                                {log.details && Object.keys(log.details).length > 0 ? (
                                  <span className="font-mono">
                                    {JSON.stringify(log.details).substring(0, 100)}
                                    {JSON.stringify(log.details).length > 100 && '...'}
                                  </span>
                                ) : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      {/* Pagination Controls */}
                      {logsTotalCount > LOGS_PER_PAGE && (
                        <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
                          <div className="flex flex-1 justify-between sm:hidden">
                            <button
                              onClick={() => loadAuditLogs(logsPage - 1)}
                              disabled={logsPage === 1}
                              className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Previous
                            </button>
                            <button
                              onClick={() => loadAuditLogs(logsPage + 1)}
                              disabled={logsPage * LOGS_PER_PAGE >= logsTotalCount}
                              className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Next
                            </button>
                          </div>
                          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                            <div>
                              <p className="text-sm text-gray-700">
                                Showing{' '}
                                <span className="font-medium">{(logsPage - 1) * LOGS_PER_PAGE + 1}</span>
                                {' '}-{' '}
                                <span className="font-medium">
                                  {Math.min(logsPage * LOGS_PER_PAGE, logsTotalCount)}
                                </span>
                                {' '}of{' '}
                                <span className="font-medium">{logsTotalCount}</span>
                                {' '}results
                              </p>
                            </div>
                            <div>
                              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                                <button
                                  onClick={() => loadAuditLogs(logsPage - 1)}
                                  disabled={logsPage === 1}
                                  className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <span className="sr-only">Previous</span>
                                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                    <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                                  </svg>
                                </button>
                                <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300">
                                  Page {logsPage} of {Math.ceil(logsTotalCount / LOGS_PER_PAGE)}
                                </span>
                                <button
                                  onClick={() => loadAuditLogs(logsPage + 1)}
                                  disabled={logsPage * LOGS_PER_PAGE >= logsTotalCount}
                                  className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <span className="sr-only">Next</span>
                                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                    <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                                  </svg>
                                </button>
                              </nav>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default PublicPortalSettings;
